import React, { createContext, useContext, useState, useEffect } from 'react';
import { useFirebase } from './FirebaseContext';

interface User {
  id: string;
  email: string;
  role: 'student' | 'instructor' | 'admin';
  firstName: string;
  lastName: string;
}

interface AuthContextType {
  user: User | null;
  login: (email: string, password: string, role: string) => Promise<boolean>;
  register: (userData: {
    email: string;
    password: string;
    firstName: string;
    lastName: string;
    phone?: string;
    address: string;
    postcode: string;
    gearbox: string;
    role: 'student' | 'instructor' | 'admin';
  }) => Promise<boolean>;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const firebase = useFirebase();

  useEffect(() => {
    // Check if user is logged in on mount
    const storedUser = localStorage.getItem('user');
    if (storedUser) {
      setUser(JSON.parse(storedUser));
    }

    // If there's a Firebase user and no local user, fetch user data
    if (firebase.currentUser && !user) {
      const fetchUserData = async () => {
        try {
          const email = firebase.currentUser?.email;
          if (email) {
            const userData = await firebase.getUserByEmail(email);
            if (userData) {
              // Ensure role is present, default to student if missing
              const role = userData.role || 'student';
              console.log("User data from Firestore:", userData);
              
              const userWithRole: User = {
                id: userData.id,
                email: userData.email,
                role: role as 'student' | 'instructor' | 'admin',
                firstName: userData.firstName || '',
                lastName: userData.lastName || ''
              };
              
              setUser(userWithRole);
              localStorage.setItem('user', JSON.stringify(userWithRole));
            }
          }
        } catch (error) {
          console.error("Error fetching user data:", error);
        }
      };
      
      fetchUserData();
    }
  }, [firebase.currentUser]);

  const login = async (email: string, password: string, role: string): Promise<boolean> => {
    try {
      console.log("Login attempt with role:", role);
      // First, authenticate with Firebase
      const firebaseUser = await firebase.loginWithEmail(email, password);
      
      // If in demo mode or if Firebase login successful
      if (firebase.isDemoMode || firebaseUser) {
        // Get user data from Firestore
        let userData = await firebase.getUserByEmail(email);
        
        if (userData) {
          console.log("User found in Firestore:", userData);
          
          // If role is undefined in Firestore, update it with the specified role
          if (!userData.role) {
            console.log("User role is undefined, updating to:", role);
            userData.role = role;
            // Update the user in Firestore with the role
            try {
              const db = firebase.getFirestore();
              if (db) {
                await firebase.updateUser(userData.id, { role });
                console.log("Updated user role in Firestore");
              }
            } catch (updateError) {
              console.error("Failed to update user role:", updateError);
            }
          }
          
          // Check if the role matches
          if (userData.role !== role) {
            console.error("Role mismatch. User is a", userData.role, "but tried to log in as", role);
            return false;
          }
          
          const userWithRole: User = {
            id: userData.id,
            email: userData.email,
            role: userData.role,
            firstName: userData.firstName || '',
            lastName: userData.lastName || ''
          };
          
          setUser(userWithRole);
          localStorage.setItem('user', JSON.stringify(userWithRole));
          return true;
        } else if (firebase.isDemoMode) {
          // If in demo mode and user not found in Firestore, create a dummy user
          console.log("Demo mode: Creating dummy user with role:", role);
          const demoUser: User = {
            id: crypto.randomUUID(),
            email: email,
            role: role as 'student' | 'instructor' | 'admin',
            firstName: role === 'admin' ? 'Demo Admin' : role === 'instructor' ? 'Demo Instructor' : 'Demo Student',
            lastName: ''
          };
          
          setUser(demoUser);
          localStorage.setItem('user', JSON.stringify(demoUser));
          return true;
        }
      }
      
      return false;
    } catch (error) {
      console.error("Login error:", error);
      return false;
    }
  };

  const register = async (userData: {
    email: string;
    password: string;
    firstName: string;
    lastName: string;
    phone?: string;
    address: string;
    postcode: string;
    gearbox: string;
    area?: string;
    instructorId?: string;
    role: 'student' | 'instructor' | 'admin';
  }): Promise<boolean> => {
    try {
      // First, create the user in Firebase Authentication
      const firebaseUser = await firebase.registerWithEmail(
        userData.email, 
        userData.password, 
        userData.firstName, 
        userData.lastName,
        userData.role,
        userData.phone,
        userData.address,
        userData.postcode,
        userData.gearbox,
        userData.area,
        userData.instructorId
      );
      
      // Then, store user data in Firestore
      const userForFirestore = {
        id: firebaseUser?.uid,
        email: userData.email,
        firstName: userData.firstName,
        lastName: userData.lastName,
        phone: userData.phone || "",
        address: userData.address,
        postcode: userData.postcode,
        gearbox: userData.gearbox,
        area: userData.area || "",
        instructorId: userData.instructorId || "",
        role: userData.role,
        createdAt: new Date().toISOString(),
        status: 'active',
        displayName: `${userData.firstName} ${userData.lastName}`
      };
      
      await firebase.createUser(userForFirestore);
      
      // Log the user in after registration
      const newUser: User = {
        id: firebaseUser?.uid || crypto.randomUUID(),
        email: userData.email,
        role: userData.role,
        firstName: userData.firstName,
        lastName: userData.lastName
      };
      
      setUser(newUser);
      localStorage.setItem('user', JSON.stringify(newUser));
      
      return true;
    } catch (error) {
      console.error("Registration error:", error);
      return false;
    }
  };

  const logout = () => {
    setUser(null);
    localStorage.removeItem('user');
    
    // If user is logged in with Firebase, sign out
    if (firebase.currentUser) {
      firebase.logout();
    }
  };

  return (
    <AuthContext.Provider value={{ user, login, register, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
